import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CircleDollarSignIcon, RefreshCwIcon } from 'lucide-react';
import {
  Button,
  CheckList,
  Combobox,
  CurveChart,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SectionHeader,
  SegmentedControl,
  SkeletonTable,
  Tabs,
  type PillTone,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  FLOOR_SOURCE,
  PRICING_ANALYTICS_QUERY_KEY,
  PRICING_LINES,
  PRICING_VIEW,
  PROGRAM_KIND,
  curvesFor,
  formatPct,
  formatSignedPct,
  formatUsd,
  keptShare,
  parseLines,
  readPricingAnalytics,
  syncPricingAnalytics,
  type PricingAnalytics,
  type PricingLineKey,
  type PricingProgram,
  type PricingSeverity,
  type PricingView,
  type ProgramKind,
} from '@/lib/pricing-analytics';

/** URL params: everything a shared link should reopen. */
const PARAM = { kind: 'kind', view: 'view', program: 'program', lines: 'lines' } as const;

/** Rows of the large chart's price table. */
const TABLE_HEADCOUNTS = [10, 20, 30, 40, 50, 60, 80, 100, 150, 200, 250, 300] as const;

const SEVERITY_TONE: Readonly<Record<PricingSeverity, PillTone>> = { high: 'danger', medium: 'warning', info: 'neutral' };
const FLAG_LABEL: Readonly<Record<string, string>> = {
  loss2025: '2025 below cost',
  thinMargin2025: '2025 keeps under 25%',
  gap2026: '2026 vs 2025 over 25%',
  cliff: 'Price drops as group grows',
  noTeamSize: 'No usable team size',
  addIgnored: '2025 rate ignored',
  noPerPerson2025: 'No 2025 per-person rate',
  feeAsCost: 'Fee counted as cost',
  missing: 'Missing price data',
};

/** The program table's sortable columns → row fields (flattened from at100). */
const PROGRAM_SORT_FIELDS = {
  program: 'name',
  signed: 'signedThisYear',
  margin: 'marginSetting',
  retail2024: 'retail2024',
  nonLocal2025: 'nonLocal2025',
  calc2026: 'calc2026',
  gap: 'gap2026Vs2025',
  cost: 'actualCost',
  kept2025: 'kept2025',
  kept2026: 'kept2026',
  fixed: 'calc2026Fixed',
  problems: 'problems',
} as const;
const PROGRAM_SORT_KEYS = Object.values(PROGRAM_SORT_FIELDS);
const DEFAULT_PROGRAM_SORT = { key: PROGRAM_SORT_FIELDS.signed, direction: 'desc' } as const;

const money = (value: number | null | undefined): string => (value === null || value === undefined ? '—' : formatUsd(value));
const pct = (value: number | null | undefined): string => (value === null || value === undefined ? '—' : formatPct(value));
const compactUsd = (value: number): string => (Math.abs(value) >= 1000 ? `$${Math.round(value / 1000)}k` : formatUsd(value));

/**
 * Analytics → Pricing: how each program's price grows with group size under
 * every Retail price list since 2015, the 2025 list and the 2026 calculator,
 * against actual cost. Mirrored from Salesforce every 15 minutes.
 *
 * @returns The page.
 */
export function PricingAnalyticsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const kind: ProgramKind = params.get(PARAM.kind) === PROGRAM_KIND.virtual ? PROGRAM_KIND.virtual : PROGRAM_KIND.inPerson;
  const view: PricingView = params.get(PARAM.view) === PRICING_VIEW.margin ? PRICING_VIEW.margin : PRICING_VIEW.price;
  const lines = parseLines(params.get(PARAM.lines));
  const { search, setSearch, debounced } = useUrlSearch();

  const pricing = useQuery({ queryKey: PRICING_ANALYTICS_QUERY_KEY, queryFn: ({ signal }) => readPricingAnalytics(signal) });
  const sync = useMutation({
    mutationFn: syncPricingAnalytics,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PRICING_ANALYTICS_QUERY_KEY }),
  });

  const setParam = (key: string, value: string | null): void =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value === null) next.delete(key);
      else next.set(key, value);
      return next;
    });

  const programs = useMemo(() => (pricing.data?.programs ?? []).filter((p) => p.kind === kind), [pricing.data, kind]);
  const selected = programs.find((p) => p.id === params.get(PARAM.program)) ?? programs[0];
  const needle = debounced.trim().toLowerCase();
  const matching = needle ? programs.filter((p) => p.name.toLowerCase().includes(needle)) : programs;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="Pricing"
        subtitle="How each program's price grows with group size: every Retail price list since 2015, the 2025 list and the 2026 calculator, against actual cost."
        action={
          <Button variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCwIcon aria-hidden className="size-4" />
            {sync.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
        }
      />

      {pricing.isError ? (
        <ErrorNotice title="Pricing unavailable" message={errorMessage(pricing.error)} />
      ) : pricing.isPending ? (
        <SkeletonTable label="Loading pricing…" />
      ) : pricing.data.programs.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<CircleDollarSignIcon aria-hidden className="size-6" />}
            title="No pricing synced yet"
            message="Price books, pricing models and cost lines sync from Salesforce every 15 minutes. Sync now to pull them immediately."
          />
        </Panel>
      ) : (
        <div className="space-y-6">
          <SyncNote data={pricing.data} syncError={sync.isError ? errorMessage(sync.error) : null} />

          <div className="flex flex-wrap items-end justify-between gap-4">
            <Tabs
              label="Program type"
              items={[
                { key: PROGRAM_KIND.inPerson, label: 'In-person programs' },
                { key: PROGRAM_KIND.virtual, label: 'Virtual programs' },
              ]}
              active={kind}
              onChange={(next) =>
                setParams((previous) => {
                  const nextParams = new URLSearchParams(previous);
                  nextParams.set(PARAM.kind, next);
                  nextParams.delete(PARAM.program);
                  return nextParams;
                })
              }
            />
            <SegmentedControl
              label="Charts show"
              options={[
                { key: PRICING_VIEW.price, label: 'Price' },
                { key: PRICING_VIEW.margin, label: 'Margin %' },
              ]}
              value={view}
              onChange={(next) => setParam(PARAM.view, next)}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-6">
              {selected ? (
                <ProgramDetail
                  program={selected}
                  programs={programs}
                  data={pricing.data}
                  lines={lines}
                  view={view}
                  onPick={(id) => setParam(PARAM.program, id)}
                />
              ) : (
                <Panel>
                  <EmptyState title="No programs of this type sold this year" message="Programs appear here once a contract is signed." />
                </Panel>
              )}
            </div>
            <Panel className="h-fit p-5">
              <SectionHeader as="h2" title="Lines" description="Tick the price lists to compare. Prices are totals for the whole group." />
              <CheckList<PricingLineKey>
                label="Lines on the charts"
                options={PRICING_LINES.map((spec) => ({ key: spec.key, label: spec.label, detail: spec.detail }))}
                values={lines}
                onToggle={(key, checked) => {
                  const next = new Set(lines);
                  if (checked) next.add(key);
                  else next.delete(key);
                  setParam(PARAM.lines, [...next].join(','));
                }}
              />
            </Panel>
          </div>

          <div>
            <SectionHeader
              as="h2"
              title="All programs"
              description="One chart per program sold this year, best sellers first. Pick one to see it large."
              aside={<span className="text-portal-micro text-portal-muted">{matching.length} of {programs.length}</span>}
            />
            <SearchInput
              label="Search programs"
              placeholder="Program name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="mb-4 w-full max-w-sm"
            />
            {matching.length === 0 ? (
              <Panel>
                <EmptyState message="No programs match that search." />
              </Panel>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {matching.map((program) => (
                  <Panel key={program.id} className={program.id === selected?.id ? 'border-brand p-4' : 'p-4'}>
                    <Button
                      variant="ghost"
                      size="compact"
                      onClick={() => {
                        setParam(PARAM.program, program.id);
                        document.getElementById('pricing-program')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      aria-label={`Show ${program.name}`}
                    >
                      {program.name}
                    </Button>
                    <p className="px-2 text-portal-micro text-portal-muted">
                      {program.signedThisYear} signed in {pricing.data.soldSince.slice(0, 4)}
                      {program.marginSetting === null ? '' : ` · ${program.marginSetting}% margin setting`}
                    </p>
                    <CurveChart
                      title={`${program.name}: ${view === PRICING_VIEW.price ? 'price' : 'margin'} by group size`}
                      categories={pricing.data.headcounts.map(String)}
                      categoryUnit="people"
                      curves={curvesFor(program, lines, view)}
                      height={150}
                      compact
                      formatValue={view === PRICING_VIEW.price ? compactUsd : formatPct}
                    />
                  </Panel>
                ))}
              </div>
            )}
          </div>

          <ProgramTable programs={matching} probe={pricing.data.probe} onPick={(id) => setParam(PARAM.program, id)} />
        </div>
      )}
    </section>
  );
}

/** When the data last synced, and whether the game-show floor is assumed. */
function SyncNote({ data, syncError }: { readonly data: PricingAnalytics; readonly syncError: string | null }): React.ReactElement {
  return (
    <div className="space-y-2">
      <p className="text-portal-micro text-portal-muted">
        {data.syncedAt ? `Synced from Salesforce ${new Date(data.syncedAt).toLocaleString()}.` : 'Not fully synced yet.'} Programs
        sold since {new Date(`${data.soldSince}T00:00:00`).toLocaleDateString()}; iPad variants left out.
        {data.floorSource === FLOOR_SOURCE.assumed
          ? ' The game-show market floor ($4,300 + $22.50 a person) is assumed: Salesforce hides those fields from the integration user.'
          : ''}
      </p>
      {syncError ? <ErrorNotice title="Sync failed" message={syncError} /> : null}
    </div>
  );
}

interface ProgramDetailProps {
  readonly program: PricingProgram;
  readonly programs: readonly PricingProgram[];
  readonly data: PricingAnalytics;
  readonly lines: ReadonlySet<PricingLineKey>;
  readonly view: PricingView;
  readonly onPick: (id: string) => void;
}

/** The large chart for one program, its price table, problems and cost lines. */
function ProgramDetail({ program, programs, data, lines, view, onPick }: ProgramDetailProps): React.ReactElement {
  const curves = curvesFor(program, lines, view);
  const shown = PRICING_LINES.filter((spec) => lines.has(spec.key) && program.lines[spec.key] !== undefined);
  const facts = [
    program.nonLocal2025
      ? `2025: ${formatUsd(program.nonLocal2025.base)}${program.nonLocal2025.rate !== null ? ` up to ${program.nonLocal2025.included ?? 30} people, then ${formatUsd(program.nonLocal2025.rate)} per extra person` : ' at any size'}`
      : 'Not on the 2025 list',
    program.base2026 !== null ? `2026 minimum ${formatUsd(program.base2026)}` : null,
    program.marginSetting !== null ? `${program.marginSetting}% margin setting` : 'No 2026 pricing model',
    `default team size ${program.teamSizeUsed}`,
  ].filter(Boolean);

  return (
    <Panel className="p-5">
      <div id="pricing-program" className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <SectionHeader as="h2" title={program.name} description={facts.join(' · ')} />
          <div className="w-full max-w-sm">
            <Combobox
              id="pricing-program-picker"
              placeholder="Search programs"
              options={[...programs]
                .sort((a, b) => b.signedThisYear - a.signedThisYear || a.name.localeCompare(b.name))
                .map((p) => ({ value: p.id, label: `${p.name} (${p.signedThisYear} signed)` }))}
              value={program.id}
              onChange={(id) => {
                if (id) onPick(id);
              }}
            />
          </div>
        </div>
        <CurveChart
          title={`${program.name}: ${view === PRICING_VIEW.price ? 'price' : 'share kept after actual cost'} by group size`}
          categories={data.headcounts.map(String)}
          categoryUnit="people"
          curves={curves}
          height={300}
          showLegend
          formatValue={view === PRICING_VIEW.price ? formatUsd : formatPct}
        />
        {program.flags.length > 0 ? (
          <ul className="space-y-2" aria-label="Problems">
            {program.flags.map((flag) => (
              <li key={`${flag.key}-${flag.text}`} className="flex flex-wrap items-baseline gap-2 text-portal-small">
                <Pill tone={SEVERITY_TONE[flag.severity]}>{FLAG_LABEL[flag.key] ?? flag.key}</Pill>
                <span>{flag.text}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <DataTable
          headers={['People', ...shown.map((spec) => spec.label), 'We keep, 2026 today', 'We keep, 2026 fixed']}
          rightAlignedHeaders={[...Array(shown.length + 3).keys()].slice(1)}
          minWidth={640}
        >
          {TABLE_HEADCOUNTS.map((people) => {
            const i = data.headcounts.indexOf(people);
            const cost = program.lines.actualCost?.[i] ?? null;
            return (
              <tr key={people} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small font-bold">{people}</td>
                {shown.map((spec) => {
                  const value = program.lines[spec.key]?.[i] ?? null;
                  const belowCost = !spec.isCost && value !== null && cost !== null && value < cost;
                  return (
                    <td key={spec.key} className={`p-3 text-right text-portal-small whitespace-nowrap ${belowCost ? 'text-portal-danger' : ''}`}>
                      {money(value)}
                    </td>
                  );
                })}
                <td className="p-3 text-right text-portal-small whitespace-nowrap">{pct(keptShare(program.lines.calc2026?.[i], cost))}</td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">{pct(keptShare(program.lines.calc2026Fixed?.[i], cost))}</td>
              </tr>
            );
          })}
        </DataTable>
        {program.costLines.length > 0 ? (
          <DataTable headers={['2026 cost line', 'Amount', 'Charged', 'Type']} rightAlignedHeaders={[1]} minWidth={520}>
            {program.costLines.map((line) => (
              <tr key={`${line.name}-${line.basis}`} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">{line.name}</td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">{formatUsd(line.amount)}</td>
                <td className="p-3 text-portal-small text-portal-muted">
                  {line.basis === 'Per Team' && line.teamsPerCost !== null && line.teamsPerCost > 1 ? `Per ${line.teamsPerCost} teams` : line.basis}
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{line.type}</td>
              </tr>
            ))}
          </DataTable>
        ) : null}
      </div>
    </Panel>
  );
}

/** Every program at the probe headcount, sortable, with the share kept against actual cost. */
function ProgramTable({
  programs,
  probe,
  onPick,
}: {
  readonly programs: readonly PricingProgram[];
  readonly probe: number;
  readonly onPick: (id: string) => void;
}): React.ReactElement {
  const { sort, onSort } = useTableSort(PROGRAM_SORT_KEYS, DEFAULT_PROGRAM_SORT);
  const rows = sortRows(
    programs.map((p) => ({ ...p.at100, id: p.id, name: p.name, signedThisYear: p.signedThisYear, marginSetting: p.marginSetting, problems: p.flags.length })),
    sort,
  );
  return (
    <div>
      <SectionHeader
        as="h2"
        title={`Programs at ${probe} people`}
        description="We keep is the share of the price left after actual cost (per-person fees left out)."
      />
      <DataTable
        headers={[
          { label: 'Program', sortKey: PROGRAM_SORT_FIELDS.program },
          { label: 'Signed', sortKey: PROGRAM_SORT_FIELDS.signed },
          { label: 'Margin setting', sortKey: PROGRAM_SORT_FIELDS.margin },
          { label: '2024', sortKey: PROGRAM_SORT_FIELDS.retail2024 },
          { label: '2025', sortKey: PROGRAM_SORT_FIELDS.nonLocal2025 },
          { label: '2026 today', sortKey: PROGRAM_SORT_FIELDS.calc2026 },
          { label: '2026 vs 2025', sortKey: PROGRAM_SORT_FIELDS.gap },
          { label: 'Actual cost', sortKey: PROGRAM_SORT_FIELDS.cost },
          { label: 'We keep, 2025', sortKey: PROGRAM_SORT_FIELDS.kept2025 },
          { label: 'We keep, 2026', sortKey: PROGRAM_SORT_FIELDS.kept2026 },
          { label: '2026 fixed', sortKey: PROGRAM_SORT_FIELDS.fixed },
          { label: 'Problems', sortKey: PROGRAM_SORT_FIELDS.problems },
        ]}
        rightAlignedHeaders={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]}
        sort={sort}
        onSort={onSort}
        minWidth={1100}
      >
        {rows.map((row) => (
          <tr key={row.id} className="border-t border-portal-line align-middle">
            <td className="p-3 text-portal-small">
              <Button variant="ghost" size="compact" onClick={() => onPick(row.id)}>
                {row.name}
              </Button>
            </td>
            <td className="p-3 text-right text-portal-small">{row.signedThisYear}</td>
            <td className="p-3 text-right text-portal-small text-portal-muted">{row.marginSetting === null ? '—' : `${row.marginSetting}%`}</td>
            <td className="p-3 text-right text-portal-small whitespace-nowrap">{money(row.retail2024)}</td>
            <td className="p-3 text-right text-portal-small whitespace-nowrap">{money(row.nonLocal2025)}</td>
            <td className="p-3 text-right text-portal-small whitespace-nowrap">{money(row.calc2026)}</td>
            <td className="p-3 text-right text-portal-small whitespace-nowrap">{row.gap2026Vs2025 === null ? '—' : formatSignedPct(row.gap2026Vs2025)}</td>
            <td className="p-3 text-right text-portal-small whitespace-nowrap text-portal-muted">{money(row.actualCost)}</td>
            <td className="p-3 text-right text-portal-small">{pct(row.kept2025)}</td>
            <td className="p-3 text-right text-portal-small">{pct(row.kept2026)}</td>
            <td className="p-3 text-right text-portal-small whitespace-nowrap">{money(row.calc2026Fixed)}</td>
            <td className="p-3 text-right text-portal-small">{row.problems}</td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}
